import { useState, useEffect } from 'react';
import axios from 'axios';
import { Webhook, RefreshCw, RotateCw, CheckCircle, XCircle, AlertCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebhookLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/webhook-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Gagal mengambil webhook logs');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (logId) => {
    setResending(prev => ({ ...prev, [logId]: true }));
    try {
      const response = await axios.post(
        `${API}/webhook-logs/${logId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'success') {
        toast.success('Webhook berhasil dikirim ulang');
      } else {
        toast.error('Webhook gagal dikirim ulang');
      }
      
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim ulang webhook');
    } finally {
      setResending(prev => ({ ...prev, [logId]: false }));
    }
  };

  const handleViewDetail = async (logId) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const response = await axios.get(`${API}/webhook-logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLog(response.data);
    } catch (error) {
      toast.error('Gagal mengambil detail log');
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'dd MMM yyyy HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'success') {
      return <CheckCircle className="text-green-600" size={20} />;
    }
    return <XCircle className="text-red-600" size={20} />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 flex items-center gap-3">
            <Webhook className="text-green-600" size={40} />
            Webhook Logs
          </h1>
          <p className="text-gray-600 mt-2">Monitor dan resend webhook notifications</p>
        </div>
        <Button
          data-testid="refresh-logs-button"
          onClick={fetchLogs}
          className="bg-green-600 hover:bg-green-700"
        >
          <RefreshCw className="mr-2" size={18} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Logs</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{logs.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Webhook className="text-blue-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Success</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {logs.filter(l => l.status === 'success').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="text-green-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Failed</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">
                  {logs.filter(l => l.status === 'failed').length}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <XCircle className="text-red-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle>Webhook Delivery Logs</CardTitle>
          <CardDescription>
            History of all webhook deliveries with retry option
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-500 text-lg">Tidak ada webhook logs</p>
              <p className="text-gray-400 text-sm mt-2">Webhook logs akan muncul saat ada pesan masuk</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold">Status</th>
                    <th className="text-left py-3 px-4 font-semibold">Timestamp</th>
                    <th className="text-left py-3 px-4 font-semibold">Webhook URL</th>
                    <th className="text-left py-3 px-4 font-semibold">HTTP Code</th>
                    <th className="text-left py-3 px-4 font-semibold">Attempts</th>
                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr 
                      key={log.id} 
                      className="border-b hover:bg-gray-50"
                      data-testid={`log-row-${log.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(log.status)}
                          <Badge
                            variant={log.status === 'success' ? 'default' : 'destructive'}
                            className={log.status === 'success' ? 'bg-green-600' : 'bg-red-600'}
                          >
                            {log.status}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono max-w-xs truncate">
                        {log.webhookUrl}
                      </td>
                      <td className="py-3 px-4">
                        {log.statusCode ? (
                          <Badge variant="outline">{log.statusCode}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{log.attempts || 1}/5</Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetail(log.id)}
                            data-testid={`view-${log.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResend(log.id)}
                            disabled={resending[log.id]}
                            data-testid={`resend-${log.id}`}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            {resending[log.id] ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-1"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <RotateCw size={16} className="mr-1" />
                                Resend
                              </>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Webhook Log Detail</DialogTitle>
            <DialogDescription>
              Complete webhook delivery information
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : selectedLog && (
            <div className="space-y-4">
              {/* Status */}
              <div>
                <Label className="text-gray-600 font-semibold">Status</Label>
                <div className="mt-2 flex items-center gap-2">
                  {getStatusIcon(selectedLog.status)}
                  <Badge
                    variant={selectedLog.status === 'success' ? 'default' : 'destructive'}
                    className={selectedLog.status === 'success' ? 'bg-green-600 text-lg px-4 py-1' : 'bg-red-600 text-lg px-4 py-1'}
                  >
                    {selectedLog.status}
                  </Badge>
                </div>
              </div>

              {/* Webhook URL */}
              <div>
                <Label className="text-gray-600 font-semibold">Webhook URL</Label>
                <div className="mt-2 bg-gray-100 p-3 rounded-lg font-mono text-sm break-all">
                  {selectedLog.webhookUrl}
                </div>
              </div>

              {/* Timestamps & Attempts */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 font-semibold">First Attempt</Label>
                  <div className="mt-2 text-sm">{formatTimestamp(selectedLog.timestamp)}</div>
                </div>
                <div>
                  <Label className="text-gray-600 font-semibold">Last Attempt</Label>
                  <div className="mt-2 text-sm">{formatTimestamp(selectedLog.lastAttempt || selectedLog.timestamp)}</div>
                </div>
                <div>
                  <Label className="text-gray-600 font-semibold">Attempts</Label>
                  <div className="mt-2">
                    <Badge variant="outline" className="text-lg">
                      {selectedLog.attempts || 1} / 5
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-600 font-semibold">HTTP Status Code</Label>
                  <div className="mt-2">
                    {selectedLog.statusCode ? (
                      <Badge variant="outline" className="text-lg">{selectedLog.statusCode}</Badge>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Payload */}
              <div>
                <Label className="text-gray-600 font-semibold">Payload Sent</Label>
                <div className="mt-2 bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-xs">
                    {JSON.stringify(selectedLog.payload, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Response */}
              {selectedLog.response && (
                <div>
                  <Label className="text-gray-600 font-semibold">Response Data</Label>
                  <div className="mt-2 bg-blue-50 border border-blue-200 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-xs text-blue-900 whitespace-pre-wrap break-words">
                      {selectedLog.response}
                    </pre>
                  </div>
                </div>
              )}

              {/* Error */}
              {selectedLog.error && (
                <div>
                  <Label className="text-gray-600 font-semibold">Error Message</Label>
                  <div className="mt-2 bg-red-50 border border-red-200 p-4 rounded-lg">
                    <p className="text-sm text-red-800 break-words">
                      {selectedLog.error}
                    </p>
                  </div>
                </div>
              )}

              {/* Auto Retry Info */}
              {selectedLog.status === 'failed' && selectedLog.attempts < 5 && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Auto-Retry:</strong> This webhook will be automatically retried in background. 
                    Next retry scheduled based on exponential backoff (5min, 15min, 30min, 60min).
                  </p>
                </div>
              )}

              {selectedLog.attempts >= 5 && (
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Max Attempts Reached:</strong> This webhook has been tried 5 times and failed. 
                    No more automatic retries will be attempted. Please check your webhook endpoint.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
            {selectedLog && selectedLog.status === 'failed' && (
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleResend(selectedLog.id);
                }}
                className="bg-green-600 hover:bg-green-700"
                disabled={resending[selectedLog.id]}
              >
                <RotateCw size={16} className="mr-2" />
                Resend Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhookLogs;
